import { crmConnectionSchema, crmSettingsSchema, type CrmSettingsView } from '@smarton-pbx/shared';
import { Link2, Unlink, Users } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, photoUrl } from '../api';
import { Avatar, EmptyState, Field, Loading, Notice, PageHeader, Pill } from '../components/ui';
import { message, rich, t } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';
import { SettingsTabs } from './SettingsTabs';

/**
 * SmartOn, the company's CRM: calls go onto its customers' call logs, answered
 * sales calls become sales events there, and SmartOn shows pop-ups and sends
 * people here to call and answer. Admins only.
 */
export function CrmSettings() {
  const settings = useLoad(api.crmSettings);
  return (
    <div className="stack-lg">
      <SettingsTabs />
      <PageHeader title="SmartOn" description={t('Calls on your customers in SmartOn, sales calls as sales events, and calling from SmartOn.')} />
      {settings.error && <Notice kind="error">{settings.error}</Notice>}
      {!settings.data ? (
        <Loading />
      ) : (
        <>
          <ConnectionCard settings={settings.data} onChanged={settings.setData} />
          {settings.data.url && <LinksCard key={settings.data.connectedAt ?? ''} settings={settings.data} onSaved={settings.setData} />}
        </>
      )}
    </div>
  );
}

function ConnectionCard({ settings, onChanged }: { settings: CrmSettingsView; onChanged: (s: CrmSettingsView) => void }) {
  const [url, setUrl] = useState(settings.url ?? '');
  const [key, setKey] = useState('');
  const [editing, setEditing] = useState(!settings.url);
  const form = useSubmit();

  async function connect(event: FormEvent) {
    event.preventDefault();
    const result = await form.submit(crmConnectionSchema, { url: url.trim(), key: key.trim() || null }, api.connectCrm);
    if (result) {
      setKey('');
      setEditing(false);
      onChanged(result);
    }
  }

  async function disconnect() {
    if (!confirm(t('Disconnect SmartOn? Calls stop going to SmartOn, and SmartOn can’t start calls here.'))) return;
    form.setError(undefined);
    try {
      onChanged(await api.disconnectCrm());
      setUrl('');
      setEditing(true);
    } catch (error) {
      form.setError(error instanceof Error ? error.message : t('Something went wrong. Try again.'));
    }
  }

  return (
    <form className="card" onSubmit={connect}>
      <div className="card-header">
        <h2>{t('Connection')}</h2>
        {settings.url &&
          (settings.error ? (
            <Pill kind="danger" dot>
              {t('Not reachable')}
            </Pill>
          ) : (
            <Pill kind="success" dot>
              {t('Connected')}
            </Pill>
          ))}
      </div>
      <div className="card-body stack">
        {settings.url && !editing ? (
          <>
            <p>
              <strong>{settings.company ?? 'SmartOn'}</strong> <span className="subtle">· {settings.url}</span>
            </p>
            {settings.error && <Notice kind="error">{message(settings.error)}</Notice>}
          </>
        ) : (
          <>
            <p className="muted">
              {rich('In SmartOn, open Settings → Integrations → API keys and create a key that may use {product}. Then enter your company’s SmartOn address and the key here.', {
                product: <strong>SmartOn PBX</strong>,
              })}
            </p>
            <div className="form-grid">
              <Field label={t('SmartOn address')} error={form.fields.url}>
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourcompany.smarton.app" data-testid="crm-url" />
              </Field>
              <Field label={t('API key')} error={form.fields.key} hint={settings.hasKey ? t('Leave empty to keep the saved key.') : undefined}>
                <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="smk_…" data-testid="crm-key" />
              </Field>
            </div>
          </>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="card-footer">
        {settings.url && !editing ? (
          <>
            <button type="button" className="secondary" onClick={() => setEditing(true)}>
              {t('Change')}
            </button>
            <button type="button" className="secondary" onClick={() => void disconnect()} disabled={form.busy}>
              <Unlink size={16} aria-hidden /> {t('Disconnect')}
            </button>
          </>
        ) : (
          <>
            <button type="submit" disabled={form.busy} data-testid="crm-connect">
              <Link2 size={16} aria-hidden /> {t('Connect')}
            </button>
            {settings.url && (
              <button type="button" className="secondary" onClick={() => (setEditing(false), setUrl(settings.url ?? ''), setKey(''))}>
                {t('Cancel')}
              </button>
            )}
          </>
        )}
      </div>
    </form>
  );
}

function LinksCard({ settings, onSaved }: { settings: CrmSettingsView; onSaved: (s: CrmSettingsView) => void }) {
  const [users, setUsers] = useState(settings.users.map((u) => ({ id: u.id, crmPersonId: u.crmPersonId, salesPerson: u.salesPerson })));
  const [queues, setQueues] = useState(settings.queues.map((q) => ({ id: q.id, sales: q.sales })));
  const [saleEventTypeId, setSaleEventTypeId] = useState(settings.saleEventTypeId);
  const [saved, setSaved] = useState(false);
  const form = useSubmit();
  const reachable = !settings.error;
  const people = [...settings.people].sort((a, b) => a.name.localeCompare(b.name));

  const changeUser = (id: string, change: Partial<(typeof users)[number]>) => {
    setSaved(false);
    setUsers((list) => list.map((u) => (u.id === id ? { ...u, ...change } : u)));
  };

  async function save() {
    setSaved(false);
    const result = await form.submit(crmSettingsSchema, { saleEventTypeId, users, queues }, api.updateCrmSettings);
    if (result) {
      onSaved(result);
      setSaved(true);
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>{t('Sales calls and people')}</h2>
      </div>
      <div className="card-body stack">
        <p className="muted">
          {rich(
            'Calls with SmartOn customers go to SmartOn. An answered call becomes a {salesEvent} when it came through a sales line or a salesperson took or made it. Any other call can be saved as one from SmartOn’s pop-up.',
            { salesEvent: <strong>{t('sales event')}</strong> },
          )}
        </p>
        <Field label={t('Sales event type')} hint={t('What calls become in SmartOn.')}>
          <select
            value={saleEventTypeId ?? ''}
            onChange={(e) => (setSaved(false), setSaleEventTypeId(e.target.value ? Number(e.target.value) : null))}
            disabled={!reachable}
            data-testid="crm-sale-event-type"
          >
            <option value="">{t('Phone call (SmartOn’s)')}</option>
            {settings.saleEventTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('Sales lines')} group>
          {settings.queues.length === 0 ? (
            <span className="subtle">{t('No queues yet.')}</span>
          ) : (
            <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem 1.25rem' }}>
              {settings.queues.map((queue) => {
                const on = queues.find((q) => q.id === queue.id)?.sales ?? false;
                return (
                  <label key={queue.id} className="checkbox">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => (setSaved(false), setQueues((list) => list.map((q) => (q.id === queue.id ? { ...q, sales: e.target.checked } : q))))}
                      data-testid="crm-sales-queue"
                    />
                    {queue.name}
                  </label>
                );
              })}
            </div>
          )}
        </Field>
      </div>

      {settings.users.length === 0 ? (
        <EmptyState icon={Users} title={t('No people yet')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('Person')}</th>
                <th>{t('In SmartOn')}</th>
                <th>{t('Salesperson')}</th>
              </tr>
            </thead>
            <tbody>
              {settings.users.map((user) => {
                const value = users.find((u) => u.id === user.id)!;
                const missing = value.crmPersonId !== null && reachable && !people.some((p) => p.id === value.crmPersonId);
                return (
                  <tr key={user.id} data-testid="crm-person-row">
                    <td>
                      <div className="person">
                        <Avatar name={user.name} photo={photoUrl(user.id, user.photoVersion)} />
                        <div>
                          <div className="name">{user.name}</div>
                          <div className="detail">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select
                        value={value.crmPersonId ?? ''}
                        onChange={(e) => changeUser(user.id, { crmPersonId: e.target.value ? Number(e.target.value) : null })}
                        disabled={!reachable}
                        aria-label={t('{name} in SmartOn', { name: user.name })}
                        data-testid="crm-person"
                      >
                        <option value="">{t('Not linked')}</option>
                        {missing && <option value={value.crmPersonId!}>{t('Not in SmartOn any more')}</option>}
                        {people.map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                            {person.email ? ` · ${person.email}` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={value.salesPerson}
                        onChange={(e) => changeUser(user.id, { salesPerson: e.target.checked })}
                        aria-label={t('{name} is a salesperson', { name: user.name })}
                        data-testid="crm-sales-person"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="card-body stack">
        <p className="subtle" style={{ fontSize: '0.85rem' }}>
          {t('People linked to SmartOn get its pop-ups when their phone rings, and their calls show who took them.')}
        </p>
        {form.error && <Notice kind="error">{form.error}</Notice>}
        {saved && !settings.error && <Notice kind="success">{t('Saved.')}</Notice>}
        {saved && settings.error && (
          <Notice kind="error">{t('Saved here, but SmartOn couldn’t be told the sales event type: {error}', { error: message(settings.error) })}</Notice>
        )}
      </div>
      <div className="card-footer">
        <button type="button" onClick={() => void save()} disabled={form.busy} data-testid="crm-save">
          {t('Save')}
        </button>
      </div>
    </div>
  );
}
